import { ISameCaseCaseModel, ISameCaseModel } from '../../../../interfaces/ISameCaseModel';
import {
    ISameCaseEasyUpdateCaseModel,
    ISameCaseEasyUpdateModel,
} from '../../same-case-easy-update/ISameCaseEasyUpdateModel';
import {
    ISameCaseMatchedCasesSearchModel,
    SameCaseSearchStatusEnum,
    SameCaseSortColumns,
} from '../../same-cases-admin/manage/useGetSameCasesQuery';
import { Offcanvas, Stack, Table } from 'react-bootstrap';
import { useEffect, useState } from 'react';

import { CheckmatePagination } from '../../CheckmatePagination';
import { DocumentTitle } from '../../../../components/shared/DocumentTitle';
import { IMultiSelectOption } from '../../../../interfaces/ILookup';
import { IPagedSortedSearchResponseModel } from '../../../../interfaces/PagedSortedSearch';
import { Loader } from '../../../../components/shared/Loader';
import { MainContentOverlayComponent } from '../../../../components/shared/MainContentOverlay';
import { NoResultsDisplay } from '../../../../components/quad/same-cases-admin/NoResultsDisplay';
import { PageWithAffixedHeader } from '../../../../shared/PageWithAffixedHeader';
import { SameCaseAppliedFilters } from '../../../../components/shared/filters/SameCaseAppliedFilters';
import { SameCaseCaseFileModelFilter } from '../../../../components/shared/filters/SameCaseCaseFileModelFilter';
import { SameCaseEasyUpdate } from '../../same-case-easy-update/SameCaseEasyUpdate';
import { SameCasesCaseRow } from '../../../../components/quad/same-cases/view/SameCasesCaseRow';
import { SortDirections } from '../../../../utilities/Constants';
import { SortableHeader } from '../../../../components/shared/SortableHeader';
import { ToggleIcon } from '../../../../shared/ToggleIcon';
import { ViewSameCasesSearchFilters } from '../../../../components/quad/same-cases/view/ViewSameCasesSearchFilters';
import { showErrorToast } from '../../../../shared/ToastUtils';
import { useCaseFileModelContext } from '../../../../contexts/useCaseFileModelContext';
import { useDidUpdate } from '@mantine/hooks';
import { useGetViewSameCases } from './useGetViewSameCases';
import { QuadCaseAuditControl } from '../../../../components/shared/modals/QuadCaseAuditControl';

const pageTitle = 'Same Cases';
const DEFAULT_SEARCH_ERROR_MESSAGE = 'An error occurred while searching for same case groups';
const defaultFilters = {
    pagedModel: {
        pageNumber: 1,
        pageSize: 10,
    },
    sortModel: {
        sortColumn: SameCaseSortColumns.CaseName,
        sortDirection: SortDirections.Asc,
    },
    statuses: [SameCaseSearchStatusEnum.Open],
};

export function ViewSameCasesPage() {
    const searchMutation = useGetViewSameCases();
    const { caseFileModelId, userCaseFileModelsQuery } = useCaseFileModelContext();

    const [activeKeys, setActiveKeys] = useState<string[]>([]);
    const [showFilterPane, setShowFilterPane] = useState<boolean>(false);
    const [currentFilters, setCurrentFilters] =
        useState<ISameCaseMatchedCasesSearchModel>(defaultFilters);
    const [results, setResults] =
        useState<IPagedSortedSearchResponseModel<ISameCaseModel, SameCaseSortColumns>>();

    const [sameCaseEasyUpdateGuid, setSameCaseEasyUpdateGuid] = useState<string | undefined>(
        undefined
    );

    const [selectedEasyUpdateFields, setSelectedEasyUpdateFields] = useState<IMultiSelectOption[]>(
        []
    );
    const [easyUpdateAttemptedCaseList, setEasyUpdateAttemptedCaseList] = useState<string[]>([]);

    const hasRows = (results?.results?.length ?? 0) > 0;

    const atleastOneExpanded = activeKeys.length > 0;

    const handleExpandCollapseAll = () => {
        atleastOneExpanded ? handleCollapseAll() : handleExpandAll();
    };

    const handleExpandAll = () => {
        setActiveKeys(results?.results?.map((sameCaseGroup) => sameCaseGroup.guid ?? '') ?? []);
    };

    const handleCollapseAll = () => {
        setActiveKeys([]);
    };

    const handleSortData = (key: string, order: string) => {
        const sortColumn = parseInt(key) as SameCaseSortColumns;
        const sortDirection =
            order.toUpperCase() === 'DESC' ? SortDirections.Desc : SortDirections.Asc;

        if (
            sortColumn === currentFilters.sortModel.sortColumn &&
            sortDirection === currentFilters.sortModel.sortDirection
        ) {
            return;
        }

        setCurrentFilters((prev) => ({
            ...prev,
            pagedModel: { ...prev.pagedModel, pageNumber: 1 },
            sortModel: {
                sortColumn,
                sortDirection,
            },
        }));
    };

    const handleApplyFilters = (filters: ISameCaseMatchedCasesSearchModel) => {
        searchMutation
            .mutateAsync(filters)
            .then((searchResults) => {
                setCurrentFilters(filters);
                setResults(searchResults);
                setShowFilterPane(false);
            })
            .catch((error) => {
                showErrorToast(error.message || DEFAULT_SEARCH_ERROR_MESSAGE);
            });
    };

    const handleToggleFilterPane = () => {
        setShowFilterPane((prev) => !prev);
    };

    const handlePageChange = (pageNumber: number) => {
        handleCollapseAll();
        setCurrentFilters((prev) => ({ ...prev, pagedModel: { ...prev.pagedModel, pageNumber } }));
    };

    const handlePageSizeChange = (pageSize: number) => {
        setCurrentFilters((prev) => ({
            ...prev,
            pagedModel: { ...prev.pagedModel, pageSize, pageNumber: 1 },
        }));
    };

    const handleEasyUpdateClick = (caseGuid: string) => {
        setEasyUpdateAttemptedCaseList((prev) => [...prev, caseGuid]);
        setSameCaseEasyUpdateGuid(caseGuid);
    };

    const mapSameCaseEasyUpdateCaseModelToSameCaseCaseModel = (
        sameCaseEasyUpdateCaseModel: ISameCaseEasyUpdateCaseModel
    ) => {
        return {
            canView: true,
            caseGuid: sameCaseEasyUpdateCaseModel.guid,
            caseNumber: sameCaseEasyUpdateCaseModel.caseNumber,
            disease: sameCaseEasyUpdateCaseModel.diagnosis,
            organizationGuid: sameCaseEasyUpdateCaseModel.organization?.guid ?? '',
            organizationName: sameCaseEasyUpdateCaseModel.organization?.name,
            detailedOrganizationName: sameCaseEasyUpdateCaseModel.organization?.primaryAssociatedOrganization?.name + ' - ' + sameCaseEasyUpdateCaseModel.organization?.name,
            sqScore: sameCaseEasyUpdateCaseModel.qScore,
            trialDate: sameCaseEasyUpdateCaseModel.trialDate,
        } as ISameCaseCaseModel;
    };

    const handleEasyUpdateComplete = (updatedSameCase: ISameCaseEasyUpdateModel | undefined) => {
        const foundResult = results?.results.find(
            (easyUpdateModel) => easyUpdateModel.guid === updatedSameCase?.sameCaseGuid
        );

        if (foundResult) {
            updatedSameCase?.caseModels.forEach((cm) => {
                const existIndex =
                    foundResult.sameCaseCaseModels
                        ?.map((ecm) => ecm.caseGuid)
                        .indexOf(cm.guid ?? '') ?? -1;

                if (existIndex > -1) {
                    const merged = mapSameCaseEasyUpdateCaseModelToSameCaseCaseModel(cm);

                    merged.canEdit = cm.canEdit;
                    merged.caseName = cm.caseName ?? '';
                    merged.status = cm.caseStatus?.displayName ?? '';

                    foundResult.sameCaseCaseModels?.splice(existIndex, 1, merged);
                }
            });

            setResults((prev) => {
                if (!prev) return prev;

                const foundIdx = prev?.results.findIndex(
                    (res) => res.guid === updatedSameCase?.sameCaseGuid
                );

                if (foundIdx && foundIdx > -1) {
                    prev?.results.splice(foundIdx, 1, foundResult);
                }

                return prev;
            });
        }

        setSameCaseEasyUpdateGuid(undefined);
    };

    const handleSelectedFieldsChange = (selectedFields: IMultiSelectOption[]) => {
        setSelectedEasyUpdateFields(selectedFields);
    };

    useDidUpdate(() => {
        searchMutation
            .mutateAsync(currentFilters)
            .then((searchResults) => {
                setResults(searchResults);
            })
            .catch((error) => {
                showErrorToast(error.message || DEFAULT_SEARCH_ERROR_MESSAGE);
            });
    }, [
        currentFilters.pagedModel.pageSize,
        currentFilters.pagedModel.pageNumber,
        currentFilters.sortModel.sortColumn,
        currentFilters.sortModel.sortDirection,
    ]);

    useEffect(() => {
        if (caseFileModelId) {
            const updatedFilters = { ...defaultFilters, caseFileModelId };
            setCurrentFilters(updatedFilters);
            searchMutation
                .mutateAsync(updatedFilters)
                .then((searchResults) => {
                    setResults(searchResults);
                })
                .catch((error) => {
                    showErrorToast(error.message || DEFAULT_SEARCH_ERROR_MESSAGE);
                });
        }
    }, [caseFileModelId]);

    return (
        <>
            <DocumentTitle title={`Quad - ${pageTitle}`} hideZoneName />
            <Offcanvas
                show={showFilterPane}
                onHide={() => setShowFilterPane(false)}
                placement="end"
                scroll
                backdrop
            >
                <Offcanvas.Header
                    closeButton
                    style={{ borderBottom: '1px solid lightgray', height: '77px' }}
                >
                    <Offcanvas.Title>
                        <span style={{ fontSize: 20, fontWeight: 500 }}>{pageTitle} Filters</span>
                    </Offcanvas.Title>
                </Offcanvas.Header>
                <Offcanvas.Body>
                    <ViewSameCasesSearchFilters
                        currentFilters={currentFilters}
                        isSearching={searchMutation.isLoading}
                        onApplyFilters={handleApplyFilters}
                    />
                </Offcanvas.Body>
            </Offcanvas>

            {sameCaseEasyUpdateGuid && (
                <MainContentOverlayComponent>
                    <SameCaseEasyUpdate
                        defaultSelectedFields={selectedEasyUpdateFields}
                        sameCaseGuid={sameCaseEasyUpdateGuid}
                        onCancel={() => setSameCaseEasyUpdateGuid(undefined)}
                        onComplete={handleEasyUpdateComplete}
                        onSelectedFieldsChange={handleSelectedFieldsChange}
                    />
                </MainContentOverlayComponent>
            )}

            <SameCaseCaseFileModelFilter
                currentFilters={currentFilters}
                setCurrentFilters={setCurrentFilters}
            />

            <PageWithAffixedHeader
                hideHeaderBottomPadding
                headerComponent={
                    <>
                        <h1 style={{ margin: 0 }} className="mb-2">
                            {pageTitle}
                        </h1>
                        <SameCaseAppliedFilters
                            currentFilters={currentFilters}
                            onApplyFilters={handleApplyFilters}
                            onToggleFilterPane={handleToggleFilterPane}
                        />
                    </>
                }
            >
                {(searchMutation.isLoading || userCaseFileModelsQuery?.isFetching) && <Loader />}

                <Table size="sm">
                    <thead>
                        <tr style={{ verticalAlign: 'middle' }}>
                            <th style={{ width: '20px' }}>
                                <ToggleIcon
                                    onClick={handleExpandCollapseAll}
                                    isOpen={atleastOneExpanded}
                                />
                            </th>
                            <SortableHeader
                                active={
                                    currentFilters.sortModel.sortColumn ===
                                    SameCaseSortColumns.CaseNumber
                                }
                                currentSortModel={results?.sortModel}
                                headerText="Case Number"
                                sortKey={SameCaseSortColumns.CaseNumber.toString()}
                                onSort={handleSortData}
                                noWrap
                                thStyle={{ width: 200 }}
                            />
                            <SortableHeader
                                active={
                                    currentFilters.sortModel.sortColumn ===
                                    SameCaseSortColumns.CaseName
                                }
                                currentSortModel={results?.sortModel}
                                headerText="Case Name"
                                sortKey={SameCaseSortColumns.CaseName.toString()}
                                onSort={handleSortData}
                                noWrap
                                thStyle={{ width: 200 }}
                            />
                            <SortableHeader
                                active={
                                    currentFilters.sortModel.sortColumn ===
                                    SameCaseSortColumns.Jurisdiction
                                }
                                currentSortModel={results?.sortModel}
                                headerText="Jurisdiction"
                                sortKey={SameCaseSortColumns.Jurisdiction.toString()}
                                onSort={handleSortData}
                                noWrap
                            />
                            <th />
                        </tr>
                    </thead>
                    <tbody>
                        {results?.results?.map((caseGroup: ISameCaseModel) => (
                            <SameCasesCaseRow
                                activeKeys={activeKeys}
                                easyUpdateAttempted={easyUpdateAttemptedCaseList.includes(
                                    caseGroup.guid
                                )}
                                key={caseGroup.guid}
                                sameCase={caseGroup}
                                onEasyUpdateClick={handleEasyUpdateClick}
                                //onCaseHistoryClick={handleCaseHistoryClick}
                            />
                        ))}
                    </tbody>
                </Table>

                <NoResultsDisplay
                    onShowFiltersClick={handleToggleFilterPane}
                    show={!hasRows && !searchMutation.isLoading}
                />

                <CheckmatePagination
                    pagedModel={results?.pagedModel}
                    onPageChange={handlePageChange}
                    onPageSizeChange={handlePageSizeChange}
                />
            </PageWithAffixedHeader>
        </>
    );
}
